import {
    Accordion,
    Code,
    Group,
    Stack,
    Text,
} from '@mantine/core';

import {StepStatusBadge} from './StepStatusBadge.tsx';
import {ExecutionStep} from '../types.ts';
import {formatTimestampText} from '../../../utils/TableTimestamps.tsx';


// Extracted from executions/pages/EditPage.tsx during FNP-79 so the
// applications detail page can render the same step UI when an Application
// has a provisioningExecutionId (FNP-1013 renamed it from workflowId, which
// named a Workflow entity this platform has never had).
//
// ⚠️ FNP-1029: same defect as PhaseBlock — this returns a STRING, so it must
// use `formatTimestampText`. It went unnoticed longer here because a step with
// no timestamps hits the `n/a` early return, and the screenshot that exposed
// the bug happened to have only SKIPPED steps. The phase rows above them, which
// DID carry a completedAt, showed "pending → [object Object]".
const formatTimeRange = (startedAt?: string, completedAt?: string) => {
    if (!startedAt && !completedAt) {
        return 'n/a';
    }
    const start = startedAt ? formatTimestampText(startedAt) : 'pending';
    const end = completedAt ? formatTimestampText(completedAt) : 'in progress';
    return `${start} → ${end}`;
};


// FNP-1029: `input`, `output` and `errorMessage` are declared `String` on the
// GraphQL schema, but the values are produced by the workflows-api from SFN
// event payloads. When a producer writes an object instead of a serialized
// string, React interpolates it as the literal "[object Object]" — which is
// exactly what the owner saw on the provisioning progress panel. The schema
// type does not protect us here: the subscription (onUpdateExecutionStep) and
// the query return the SAME ExecutionStep type, and a subscription payload is
// merged straight into the cached step object by useOnExecutionProgress's
// mergeStep, so whatever shape arrives on the wire reaches this component
// unvalidated. These helpers make it impossible for a non-string to print as
// "[object Object]" regardless of what the producer sends.

const EMPTY = 'n/a';

/**
 * Renders a raw-data field (`input` / `output`). These are already presented in
 * a `<Code block>` — a raw-data affordance by design — so a pretty-printed
 * serialization is the correct, intended rendering for an object here rather
 * than a substitute for a human message.
 */
const asCodeText = (value: unknown): string | null => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'string') {
        return value.trim().length > 0 ? value : null;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        // circular / non-serializable payload
        return EMPTY;
    }
};

/**
 * Renders a prose field (`errorMessage` / `errorType`) shown in a red `<Text>`.
 *
 * The ticket says "do NOT fix this with JSON.stringify", written on the premise
 * that a dedicated human-readable `message` field exists to select instead. It
 * does not — `ExecutionStep` has no `message` field at all (verified against
 * workflows-api schema.graphql). So we honour the INTENT of that instruction:
 * when the value is an object we look for a human message the payload actually
 * carries (`message`, `errorMessage`, `cause`, and the SFN-native `Cause` /
 * `Error`) and show that. Serialization is the last resort only, because
 * showing a dumped payload still beats showing "[object Object]".
 */
const MESSAGE_KEYS = ['message', 'errorMessage', 'cause', 'Cause', 'Error'] as const;

const asMessageText = (value: unknown): string | null => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'string') {
        return value.trim().length > 0 ? value : null;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        for (const key of MESSAGE_KEYS) {
            const candidate = record[key];
            if (typeof candidate === 'string' && candidate.trim().length > 0) {
                return candidate;
            }
        }
        // SFN wraps the real cause in a JSON string under `Cause`; if the
        // producer already parsed it, recurse one level to find the message.
        for (const key of MESSAGE_KEYS) {
            const candidate = record[key];
            if (candidate && typeof candidate === 'object') {
                const nested = asMessageText(candidate);
                if (nested && nested !== EMPTY) {
                    return nested;
                }
            }
        }
    }
    try {
        return JSON.stringify(value);
    } catch {
        return EMPTY;
    }
};


export const StepBlock = ({step}: { step: ExecutionStep }) => {
    const errorType = asMessageText(step.errorType);
    const errorMessage = asMessageText(step.errorMessage);
    const input = asCodeText(step.input);
    const output = asCodeText(step.output);
    const label = asMessageText(step.stepName)
        ?? asMessageText(step.taskStateName)
        ?? asMessageText(step.id)
        ?? EMPTY;

    return (
        <Stack gap={'xs'}>
            <Group justify={'space-between'} wrap={'nowrap'}>
                <Group gap={'xs'} wrap={'nowrap'}>
                    <Text fw={600} size={'sm'}>{step.position}. {label}</Text>
                    <StepStatusBadge status={step.stepStatus}/>
                </Group>
                <Text c={'dimmed'} size={'xs'}>
                    {formatTimeRange(step.startedAt, step.completedAt)}
                    {typeof step.elapsedMs === 'number' ? ` · ${step.elapsedMs} ms` : ''}
                    {typeof step.retryCount === 'number' && step.retryCount > 0 ? ` · ${step.retryCount} retries` : ''}
                </Text>
            </Group>

            {errorMessage || errorType ? (
                <Stack gap={4} pl={'md'}>
                    {errorType ? (
                        <Text size={'xs'} c={'red'}>
                            <Text span fw={600}>Error type:</Text> {errorType}
                        </Text>
                    ) : null}
                    {errorMessage ? (
                        <Text size={'xs'} c={'red'}>
                            <Text span fw={600}>Error:</Text> {errorMessage}
                        </Text>
                    ) : null}
                </Stack>
            ) : null}

            {input ? (
                <Accordion variant={'separated'} chevronPosition={'left'}>
                    <Accordion.Item value={'input'}>
                        <Accordion.Control>
                            <Text size={'xs'} fw={600}>Input</Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Code block style={{whiteSpace: 'pre-wrap'}}>{input}</Code>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            ) : null}

            {output ? (
                <Accordion variant={'separated'} chevronPosition={'left'}>
                    <Accordion.Item value={'output'}>
                        <Accordion.Control>
                            <Text size={'xs'} fw={600}>Output</Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Code block style={{whiteSpace: 'pre-wrap'}}>{output}</Code>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            ) : null}
        </Stack>
    );
};
